import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as z from "zod";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { DatePicker } from "../../../../components/DatePicker/DatePicker";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../components/Error";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { LinkAttributeField } from "../../../../components/LinkAttributeField/LinkAttributeField";
import { SelectBoxV2 } from "../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../components/TextFields/TextFields";
import {
  H4,
  H6,
  SmallText,
} from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import { Form } from "../../../../layout/FormLayout";
import type {
  DataMutate,
  Language,
  OptionType,
  SupportedLanguage,
} from "../../../../types/types";
import type {
  Assets,
  ListDetailsSchema,
  ListItemObjectSchema,
  PIMProduct,
  SupportedAssetAccessibility,
  SupportedAssetCategoryType,
  SupportedAssetType,
  PIMProductStatusAck,
} from "../../../../types/types.PIM";
import { useCountriesList, useRegionsList } from "../../../../util/Locations";
import { strings } from "../../../../util/strings";
import {
  getDateTime,
  toTitleCase,
  useFormWrapper,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../util/util";
import { useAssetHostData } from "../../../../util/hooks";
import {
  zodOptionalSelectBoxDefault,
  zodOptionalString,
  zodSelectBoxDefault,
} from "../../../../util/zod.util";
import type {
  AssetCategory,
  AssetType,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { FileUploader } from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/FileUploader";
import { RadioButton } from "../../../../components/RadioButton/RadioButton";
import { DocumentAccessibility } from "../../../../components/DocumentAccessibility/DocumentAccessibility";
import {
  AssetMetadataHeading,
  AssetSourceModeController,
  accessibilityRequestData,
  assetMetadataLabelMap,
  assetSourceModeSchema,
  disableAccessibilitySubmit,
  externalAssetValidationSchema,
  getAssetName,
  NewAssetFormValues,
  NewAssetFormSubmitValues,
} from "./util";
import { AssetContainerType } from "../../../../util/assetContainer.types";
import { spacingTokens } from "../../../../designTokens";

export interface IAssetPayload {
  name: string;
  asset_type: string;
  asset_mode: Assets["asset_mode"];
  asset_host_id: string;
  category_id: string;
  language?: string;
  product_ids?: string[];
  document_url?: string | null;
  fully_external_url?: string | null;
  document_type?: string | null;
  issue_date?: string | null;
  expiration_date?: string | null;
  renewal_date?: string | null;
  country?: string | null;
  region?: string | null;
  visibility?: string;
  role_ids?: string[];
  user_ids?: string[];
  customer_ids?: string[];
  allow_all_customers?: boolean;
  allow_all_distributors?: boolean;
  allow_all_roles?: boolean;
}

const AddAssetSchemaFn = (t: TFunction) =>
  z
    .object({
      asset_name: z.string().optional(),
      asset_source_mode: assetSourceModeSchema,
      asset_host: zodSelectBoxDefault(t),
      input_asset_type: zodSelectBoxDefault(t),
      file_type: z.instanceof(File).optional(),
      external_asset: externalAssetValidationSchema(t),
      input_category_type: zodSelectBoxDefault(t),
      input_lang_type: zodSelectBoxDefault(t),
      product_id: zodOptionalSelectBoxDefault.optional(),
      issue_date: zodOptionalString,
      expiration_date: zodOptionalString,
      renewal_date: zodOptionalString,
      input_document_type: zodOptionalSelectBoxDefault.optional().nullable(),
      country: zodOptionalSelectBoxDefault.optional().nullable(),
      region: zodOptionalSelectBoxDefault.optional().nullable(),
      visibility: z.string().optional(),
      custom_category_name: z
        .string()
        .refine((val) => Boolean(val.trim()), {
          message: strings(t).thisIsARequiredField,
        })
        .nullish(),
      input_role_ids: z.array(z.string()).optional().nullable(),
      input_user_ids: z.array(z.string()).optional().nullable(),
      input_customer_ids: z.array(z.string()).optional().nullable(),
    })
    .superRefine(({ input_asset_type, input_category_type }, ctx) => {
      if (!input_asset_type?.value || !input_category_type?.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("This is a required field"),
          path: ["input_asset_type"],
        });
      }
    });

export const NewAssetForm = <ParentData,>({
  onComplete,
  product,
  products,
  assetContainerType = "product",
  refreshParentData,
  defaultAssetType,
  onAddAssetData,
  default_asset_types,
  default_categories,
  hideAccessibility,
  onCancel,
  allowUpload = false,
}: {
  defaultAssetType?: string;
  onComplete?: (productId?: string) => void;
  product?: PIMProduct;
  assetContainerType?: AssetContainerType;
  products?: OptionType<string>[];
  refreshParentData: DataMutate<ParentData>;
  onAddAssetData?: (data: NewAssetFormSubmitValues) => Promise<void>;
  default_asset_types?: OptionType<SupportedAssetType>[];
  default_categories?: OptionType<SupportedAssetCategoryType>[];
  hideAccessibility?: boolean;
  onCancel: () => void;
  allowUpload?: boolean;
}) => {
  const { t } = useTranslation();
  const countries = useCountriesList();
  const regions = useRegionsList();
  const [submitting, setSubmitting] = useState(false);
  const [assetTypes, setAssetTypes] = useState<
    OptionType<SupportedAssetType>[]
  >([]);
  const [assetCategories, setAssetCategories] = useState<
    OptionType<SupportedAssetCategoryType>[]
  >([]);

  const [documentTypesFilters, setDocumentTypesFilters] = useState<
    OptionType<string>[]
  >([]);

  const [assetHostFilters, setAssetHostFilters] = useState<
    OptionType<string>[]
  >([]);

  const [assetLanguages, setAssetLanguages] = useState<
    OptionType<SupportedLanguage>[]
  >([]);

  const { notifySuccess, notifyError } = useContext(Notifications);
  const {
    tenant_id,
    storefront_id,
    storefront_metadata: { enable_external_assets },
  } = useStoreState();

  const updateProductStatus = useUpdateProductStatus({
    product: product ?? ({ status: "archived" } as PIMProduct),
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [accessibility, setAccessibility] =
    useState<SupportedAssetAccessibility>("internal");

  const { assetsHostResponse, assetHostDefault } = useAssetHostData(tenant_id);

  const assetTypeDefault = useMemo(
    () =>
      defaultAssetType === "Documents"
        ? { label: t("Document"), value: "document" }
        : null,
    [defaultAssetType, t]
  );

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddAssetSchemaFn(t)),
    defaultValues: {
      input_asset_type: assetTypeDefault ?? {
        label: undefined,
        value: undefined,
      },
      asset_source_mode: "gcs",
      external_asset: { display_text: "", url: "" },
      custom_category_name: null,
      input_category_type: { label: undefined, value: undefined },
      input_lang_type:
        assetLanguages.length === 1
          ? assetLanguages[0]
          : { label: undefined, value: undefined },
      input_document_type: { label: undefined, value: undefined },
      country: { label: undefined, value: undefined },
      region: { label: undefined, value: undefined },
      product_id:
        products?.length === 1
          ? products[0]
          : { label: undefined, value: undefined },
      file_type: undefined,
      asset_name: undefined,
      visibility: accessibility,
      input_role_ids: [],
      input_user_ids: [],
      input_customer_ids: [],
    },
  });

  const {
    handleSubmit,
    control,
    errors,
    formState,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const selectedAssetType: string = watch("input_asset_type")?.value;
  const selectedSourceMode = watch("asset_source_mode");
  const isExternalAsset = selectedSourceMode === "external";

  const selectedCategoryType: string = watch("input_category_type")?.value;

  const input_role_ids = watch("input_role_ids") || [];
  const input_user_ids = watch("input_user_ids") || [];

  const { data: assetTypeResponse, error: assetTypeError } = useSWR<AssetType>(
    !default_asset_types ? endpoints.v2_storefronts_id_pim_asset_types() : null
  );

  const { error: categoryTypeError } = useSWR<AssetCategory>(
    selectedAssetType && !default_categories
      ? `${endpoints.v2_tenants_id_pim_assets_categories(
          tenant_id
        )}?asset_type=${assetTypeDefault?.value ?? selectedAssetType}`
      : null,
    {
      onSuccess: ({ asset_categories }) =>
        setAssetCategories(
          asset_categories.map((category) => ({
            label: category.name,
            value: category.id,
          }))
        ),
    }
  );

  const { data: documentTypesResponse, error: documentTypeError } = useSWR<{
    data: ListDetailsSchema;
  }>(
    endpoints.v2_tenants_id_pim_get_list_by_name(tenant_id, "Document Types"),
    {
      onError: (error) => {
        console.error("There was an error fetching the Document types list.", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
    }
  );

  const { data: languagesResponse, error: languageError } = useSWR<{
    languages: Language[];
  }>(endpoints.v1_storefronts_id_languages(storefront_id), {
    onSuccess: ({ languages }) => {
      setAssetLanguages(
        languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    },
    onError: () =>
      notifyError(t("could not load asset languages, Something went wrong.")),
  });

  const default_submit = async (formValues: NewAssetFormValues) => {
    const {
      asset_name,
      input_asset_type,
      input_category_type,
      issue_date,
      expiration_date,
      renewal_date,
      country,
      region,
      input_lang_type,
      input_document_type,
      input_customer_ids,
      input_user_ids,
      input_role_ids,
      external_asset,
      asset_source_mode,
      asset_host,
    } = formValues;

    let ackedProduct: PIMProductStatusAck | null = null;
    if (product?.id) {
      const { data } = await updateProductStatus();
      ackedProduct = data;
    }

    const assetType =
      input_asset_type?.value.toLowerCase() as SupportedAssetType;
    const assetName = getAssetName(
      asset_source_mode,
      asset_name,
      external_asset
    );
    const requestPayload: IAssetPayload & {
      is_downloadable?: boolean;
      visibility: SupportedAssetAccessibility;
    } = {
      name: assetName,
      asset_type: assetType,
      category_id: input_category_type?.value,
      language: input_lang_type?.value,
      visibility: accessibility,
      document_type: input_document_type?.value || null,
      asset_host_id: asset_host?.value || "",
      asset_mode: asset_source_mode,
      ...accessibilityRequestData(
        assetType,
        accessibility,
        input_customer_ids,
        input_user_ids,
        input_role_ids
      ),
    };

    if (product?.id) {
      requestPayload.product_ids = [ackedProduct!.id];
    }
    if (issue_date) {
      requestPayload.issue_date = getDateTime(issue_date).dateFullYear;
    }
    if (expiration_date) {
      requestPayload.expiration_date = getDateTime(expiration_date).dateFullYear;
    }
    if (renewal_date) {
      requestPayload.renewal_date = getDateTime(renewal_date).dateFullYear;
    }
    if (input_document_type?.value) {
      requestPayload.document_type = input_document_type?.value;
    }
    if (country?.value) {
      requestPayload.country = country?.value;
    }
    if (region?.value) {
      requestPayload.region = region?.value;
    }
    if (asset_source_mode === "external") {
      requestPayload.fully_external_url = external_asset?.url?.trim() || null;
    }

    const baseURL = endpoints.v2_storefronts_id_pim_assets(tenant_id);

    setSubmitting(true);

    try {
      const { data: createResp } = await Axios.post(baseURL, requestPayload);
      const assetId = createResp?.id ?? createResp?.data?.id;
      if (asset_source_mode === "gcs" && selectedFile && assetId) {
        const uploadUrl = endpoints.v2_tenants_id_pim_assets_id(
          tenant_id,
          assetId
        );
        const formData = new FormData();
        formData.append("file", selectedFile);
        await Axios.put(uploadUrl, formData);
      }
      await refreshParentData();
      notifySuccess(t("Asset successfully added"));
      if (onComplete) {
        onComplete(ackedProduct?.primary_staged_product_id ?? ackedProduct?.id);
      }
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not add asset. Something went wrong."),
        {
          error,
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const changeAssetAccessibility = (
    newAccessibility: string | React.FormEvent<HTMLSelectElement>
  ) => {
    if (typeof newAccessibility === "string") {
      setAccessibility(newAccessibility as SupportedAssetAccessibility);
    } else if (newAccessibility.currentTarget.value) {
      setAccessibility(
        newAccessibility.currentTarget.value as SupportedAssetAccessibility
      );
    }
  };

  const onSubmit = async (formValues: NewAssetFormValues): Promise<void> => {
    if (onAddAssetData) {
      setSubmitting(true);
      try {
        const normalizedFormValues: NewAssetFormValues = {
          ...formValues,
          issue_date: formValues.issue_date
            ? getDateTime(formValues.issue_date).dateFullYear
            : formValues.issue_date,
          expiration_date: formValues.expiration_date
            ? getDateTime(formValues.expiration_date).dateFullYear
            : formValues.expiration_date,
          renewal_date: formValues.renewal_date
            ? getDateTime(formValues.renewal_date).dateFullYear
            : formValues.renewal_date,
        };
        await onAddAssetData({
          ...normalizedFormValues,
          accessibility,
          selected_file: selectedFile,
        });
      } finally {
        setSubmitting(false);
      }
    } else {
      await default_submit(formValues);
    }
  };

  useEffect(() => {
    if (languagesResponse) {
      setAssetLanguages(
        languagesResponse.languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    }
  }, [languagesResponse]);

  useEffect(() => {
    if (documentTypesResponse) {
      setDocumentTypesFilters(
        (documentTypesResponse.data?.items ?? []).map(
          (type: ListItemObjectSchema) => {
            return { label: type.name, value: type.name };
          }
        )
      );
    }
  }, [documentTypesResponse]);

  useEffect(() => {
    if (assetsHostResponse?.data) {
      setAssetHostFilters(
        assetsHostResponse.data.items.map((type: ListItemObjectSchema) => {
          return { label: type.name, value: type.id };
        })
      );
    }
  }, [assetsHostResponse]);

  useEffect(() => {
    setValue("input_category_type", { label: undefined, value: undefined });
    setValue("file_type", undefined);
    setValue("asset_name", undefined);
    setValue("external_asset", null);
    setSelectedFile(null);
  }, [selectedAssetType, setValue]);

  useEffect(() => {
    if (selectedSourceMode === "external") {
      setValue("file_type", undefined);
      setSelectedFile(null);
      return;
    }

    setValue("external_asset", null);
  }, [register, selectedSourceMode, setValue]);

  useEffect(() => {
    if (assetTypeResponse) {
      const { asset_types } = assetTypeResponse;
      setAssetTypes(
        product?.id
          ? !assetTypeDefault
            ? asset_types.reduce<OptionType<SupportedAssetType>[]>(
                (acc, cur) =>
                  cur === "document"
                    ? acc
                    : [
                        ...acc,
                        {
                          label: toTitleCase(cur),
                          value: cur,
                        },
                      ],
                []
              )
            : []
          : asset_types.map((type) => ({
              label: toTitleCase(type),
              value: type,
            }))
      );
    }
  }, [assetTypeDefault, assetTypeResponse, product?.id]);

  const isLoadingAssetTypes =
    !assetTypeError && !assetTypeResponse && !default_asset_types;

  if (isLoadingAssetTypes) {
    return <DelayedSpinner />;
  }

  if (assetTypeError || categoryTypeError) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error loading asset types and categories. Please try again later"
        )}
      />
    );
  }

  return (
    <>
      {product?.id && (
        <div
          style={{
            margin: `${spacingTokens.spacing_3xl} ${spacingTokens.spacing_none} ${spacingTokens.spacing_xl} ${spacingTokens.spacing_none}`,
          }}
        >
          <H6>{t("Asset details")}</H6>
        </div>
      )}
      <Form id="new_asset_form_id" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_asset_type"
          autoComplete={"input_asset_type"}
          placeholder={assetMetadataLabelMap(t).asset_type}
          rules={{
            required: true,
          }}
          isDisabled={product?.id && !!assetTypeDefault}
          errors={errors}
          formState={formState}
          defaultValue={
            assetTypeDefault ?? { label: undefined, value: undefined }
          }
          options={default_asset_types ?? assetTypes}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_category_type"
          autoComplete={"input_category_type"}
          placeholder={assetMetadataLabelMap(t).asset_category}
          options={default_categories ?? assetCategories}
          rules={{
            required: true,
          }}
          errors={errors}
          isDisabled={!selectedAssetType}
          formState={formState}
        />
        {selectedCategoryType && selectedCategoryType === "other" ? (
          <TextField
            name="custom_category_name"
            label={t("Description")}
            autoComplete={"custom_category_name"}
            theref={register()}
            formState={formState}
            errors={errors}
            type="text"
          />
        ) : (
          <></>
        )}
        <AssetSourceModeController control={control} register={register} />
        {isExternalAsset ? (
          <>
            <LinkAttributeField
              name="external_asset"
              label={t("Asset")}
              displayTextLabel={t("Asset Name")}
              urlLabel={t("URL")}
              control={control}
              register={register}
              errors={errors}
              formState={formState}
              isRequired={true}
              updateAssetName={(name) => setValue("asset_name", name)}
            />
            <input ref={register()} type="hidden" name="asset_name" />
          </>
        ) : (
          <>
            <FileUploader
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              setValue={setValue}
              selectedAssetType={selectedAssetType}
              control={control}
              errors={errors}
              formState={formState}
            />
            <TextField
              name="asset_name"
              label={assetMetadataLabelMap(t).name}
              autoComplete={"asset_name"}
              theref={register({
                required: !isExternalAsset,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
          </>
        )}
        <AssetMetadataHeading />
        {!languageError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            options={assetLanguages}
            name="input_lang_type"
            autoComplete={"input_lang_type"}
            placeholder={assetMetadataLabelMap(t).language}
            rules={{
              required: true,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        {assetHostDefault && (
          <>
            {enable_external_assets ? (
              <Controller
                as={SelectBoxV2}
                control={control}
                name="asset_host"
                defaultValue={assetHostDefault}
                autoComplete={"asset_host"}
                placeholder={assetMetadataLabelMap(t).asset_host}
                options={assetHostFilters}
                rules={{
                  required: true,
                }}
                errors={errors}
                formState={formState}
              />
            ) : (
              <Controller
                name="asset_host"
                defaultValue={assetHostDefault}
                control={control}
                render={() => <input type="hidden" />}
              />
            )}
          </>
        )}
        {!documentTypeError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="input_document_type"
            autoComplete={"input_document_type"}
            isClearable={true}
            placeholder={assetMetadataLabelMap(t).document_type}
            options={documentTypesFilters}
            errors={errors}
            formState={formState}
          />
        )}
        <DatePicker
          label={assetMetadataLabelMap(t).issue_date}
          name={"issue_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={() => false}
        />
        <DatePicker
          label={assetMetadataLabelMap(t).expiration_date}
          name={"expiration_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={() => false}
        />
        <DatePicker
          label={assetMetadataLabelMap(t).renewal_date}
          name={"renewal_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={() => false}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="country"
          autoComplete={"country"}
          placeholder={assetMetadataLabelMap(t).country}
          isClearable={true}
          options={countries}
          rules={{
            required: false,
          }}
          errors={errors}
          formState={formState}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="region"
          autoComplete={"region"}
          isClearable={true}
          placeholder={assetMetadataLabelMap(t).region}
          options={regions}
          rules={{
            required: false,
          }}
          errors={errors}
          formState={formState}
        />
        {Boolean(products) && products!.length > 0 && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="product_id"
            autoComplete={"products"}
            placeholder={t("Assign to product")}
            options={products}
            rules={{
              required: false,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        {defaultAssetType &&
          !hideAccessibility &&
          (selectedAssetType === "document" ? (
            <DocumentAccessibility
              accessibility={accessibility}
              methodsOfUseForm={methodsOfUseForm}
              assetContainerType={assetContainerType}
              onAccessibilityChange={changeAssetAccessibility}
              existingData={{
                users: [],
                roles: [{ id: "allow_all_roles", name: "All Roles" }],
                customers: [
                  { id: "allow_all_customers", name: "All Customers" },
                  { id: "allow_all_distributors", name: "All Distributors" },
                ],
              }}
            />
          ) : (
            <>
              <H4>{t("Accessibility")}</H4>
              <SmallText>{t("Media is accessible by:")}</SmallText>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"public"}
                    value="public"
                    checked={accessibility === "public"}
                    disabled={
                      assetContainerType === "customer" ||
                      assetContainerType === "distributor" ||
                      assetContainerType === "supplier"
                    }
                    optionTitle={t("Public")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"logged_in_user"}
                    value="logged_in_user"
                    checked={accessibility === "logged_in_user"}
                    disabled={
                      assetContainerType === "customer" ||
                      assetContainerType === "distributor" ||
                      assetContainerType === "supplier"
                    }
                    optionTitle={t("Customers & Internal Users")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"internal"}
                    value="internal"
                    checked={accessibility === "internal"}
                    optionTitle={t("Internal Users Only")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
            </>
          ))}
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2
            fullWidth
            disabled={submitting}
            type="button"
            onClick={onCancel}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2
            fullWidth
            loading={submitting}
            disabled={
              !allowUpload &&
              !hideAccessibility &&
              disableAccessibilitySubmit(
                selectedAssetType as SupportedAssetType,
                accessibility,
                input_role_ids,
                input_user_ids
              )
            }
            type="submit"
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
};
